import codici from './CodiceFiscale.json';

const codeMap = new Map();

for (const city of codici) {
    if (!city.c || !city.n || !city.p) continue;

    codeMap.set(city.c, {
        n: city.n,
        p: city.p
    });
}

/**
 * Validates an Italian Tax Code (Codice Fiscale)
 * @param {string} taxCode - The tax code to validate
 * @returns {object|boolean} - Object with extracted data if valid, false if invalid
 */
export function validateItalianTaxCode(taxCode) {
    // Remove spaces and convert to uppercase
    const cleanCode = taxCode.replace(/\s/g, '').toUpperCase();

    // Check length
    if (cleanCode.length !== 16) {
        return false;
    }

    // Check format: 6 letters + 2 digits + 1 letter + 2 digits + 1 letter + 3 digits + 1 letter
    const pattern = /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/;
    if (!pattern.test(cleanCode)) {
        return false;
    }

    // Check month code (9th character)
    const monthCodes = 'ABCDEHLMPRST';
    const monthChar = cleanCode.charAt(8);
    if (!monthCodes.includes(monthChar)) {
        return false;
    }

    // Validate day (10th-11th characters)
    const day = parseInt(cleanCode.substring(9, 11));
    if (day < 1 || day > 71) { // 71 because females add 40 to day
        return false;
    }

    // Validate control character (last character)
    if (!validateControlCharacter(cleanCode)) {
        return false;
    }

    // If all validations pass, extract and return the data
    return extractTaxCodeData(cleanCode);
}

/**
 * Extracts data from a valid Italian Tax Code
 * @param {string} cleanCode - Clean 16-character tax code (already validated)
 * @returns {object} - Object with gender, birth_date, and birth_place_code
 */
function extractTaxCodeData(cleanCode) {
    // Extract year (7th-8th characters)
    const yearDigits = parseInt(cleanCode.substring(6, 8));
    // Determine century - assume current century for years <= current year, previous century for others
    const currentYear = new Date().getFullYear();
    const currentCentury = Math.floor(currentYear / 100) * 100;
    const fullYear = yearDigits <= (currentYear % 100) ?
        currentCentury + yearDigits :
        currentCentury - 100 + yearDigits;

    // Extract month (9th character)
    const monthCodes = 'ABCDEHLMPRST';
    const monthChar = cleanCode.charAt(8);
    const month = monthCodes.indexOf(monthChar); // 0-based month for JavaScript Date

    // Extract day and determine gender (10th-11th characters)
    const dayValue = parseInt(cleanCode.substring(9, 11));
    let day, gender;

    if (dayValue > 40) {
        // Female: day has 40 added to it
        day = dayValue - 40;
        gender = 'F';
    } else {
        // Male: day is as-is
        day = dayValue;
        gender = 'M';
    }

    // Extract birth place code (Belfiore code) (12th-15th characters)
    const birth_place_code = cleanCode.substring(11, 15);

    // Create birth date
    const birth_date = new Date(fullYear, month, day);

    const city = codeMap.get(birth_place_code)
    return {
        gender: gender,
        birth_date: birth_date,
        birth_place: {
            code: birth_place_code,
            name: city.n,
            province: city.p
        }
    };
}
/**
 * Validates the control character (checksum) of the tax code
 * @param {string} taxCode - Clean 16-character tax code
 * @returns {boolean} - True if control character is correct
 */
function validateControlCharacter(taxCode) {
    const oddPositionValues = {
        '0': 1, '1': 0, '2': 5, '3': 7, '4': 9, '5': 13, '6': 15, '7': 17, '8': 19, '9': 21,
        'A': 1, 'B': 0, 'C': 5, 'D': 7, 'E': 9, 'F': 13, 'G': 15, 'H': 17, 'I': 19, 'J': 21,
        'K': 2, 'L': 4, 'M': 18, 'N': 20, 'O': 11, 'P': 3, 'Q': 6, 'R': 8, 'S': 12, 'T': 14,
        'U': 16, 'V': 10, 'W': 22, 'X': 25, 'Y': 24, 'Z': 23
    };

    const evenPositionValues = {
        '0': 0, '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9,
        'A': 0, 'B': 1, 'C': 2, 'D': 3, 'E': 4, 'F': 5, 'G': 6, 'H': 7, 'I': 8, 'J': 9,
        'K': 10, 'L': 11, 'M': 12, 'N': 13, 'O': 14, 'P': 15, 'Q': 16, 'R': 17, 'S': 18, 'T': 19,
        'U': 20, 'V': 21, 'W': 22, 'X': 23, 'Y': 24, 'Z': 25
    };

    const remainderToLetter = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

    let sum = 0;

    // Calculate sum for first 15 characters
    for (let i = 0; i < 15; i++) {
        const char = taxCode.charAt(i);
        if (i % 2 === 0) { // Odd position (1-based indexing)
            sum += oddPositionValues[char];
        } else { // Even position (1-based indexing)
            sum += evenPositionValues[char];
        }
    }

    // Calculate expected control character
    const expectedControlChar = remainderToLetter[sum % 26];

    // Compare with actual control character
    return taxCode.charAt(15) === expectedControlChar;
}

/**
 * Extended validation with detailed error information
 * @param {string} taxCode - The tax code to validate
 * @returns {object} - Validation result with details
 */
function validateItalianTaxCodeDetailed(taxCode) {
    const result = {
        isValid: false,
        errors: [],
        cleanCode: ''
    };

    if (!taxCode || typeof taxCode !== 'string') {
        result.errors.push('Tax code must be a non-empty string');
        return result;
    }

    const cleanCode = taxCode.replace(/\s/g, '').toUpperCase();
    result.cleanCode = cleanCode;

    if (cleanCode.length !== 16) {
        result.errors.push(`Invalid length: expected 16 characters, got ${cleanCode.length}`);
        return result;
    }

    const pattern = /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/;
    if (!pattern.test(cleanCode)) {
        result.errors.push('Invalid format: must match pattern LLLLLLNNLNNLNNNL (L=letter, N=number)');
        return result;
    }

    // Check month code
    const monthCodes = 'ABCDEHLMPRST';
    const monthChar = cleanCode.charAt(8);
    if (!monthCodes.includes(monthChar)) {
        result.errors.push(`Invalid month code: '${monthChar}' (valid codes: ${monthCodes})`);
    }

    // Check day
    const day = parseInt(cleanCode.substring(9, 11));
    if (day < 1 || day > 71) {
        result.errors.push(`Invalid day: ${day} (valid range: 1-31 for males, 41-71 for females)`);
    }

    // Check control character
    if (!validateControlCharacter(cleanCode)) {
        result.errors.push('Invalid control character (checksum failed)');
    }

    result.isValid = result.errors.length === 0;
    return result;
}
